import { useEffect, useState } from 'react';
import { EST_DEF_QUERIES, estMinVal, stdMinVal } from '../constants/spread';
import { getListPrice } from 'src/common/hooks/widgets/fetchData';
import { useCurrencyWithSymbol } from 'src/common/hooks/widgets/useCurrencyWithSymbol';

/**
 * Calculate the estimated price
 *
 * @param {object} intl used for the formatting
 * @param {string} currency the language code to get the currency sign for
 * @param {number} employees : Number of employees/workstations
 * @param {boolean} breach : Includes Breach
 * @returns {Array}
 */

export interface PropsFunc {
  currency: string;
  country: string;
  queriesInput: number;
  recordsInput: number;
  filterChainsInput: number;
  monitorsInput: number;
  httpsRedirectInput?: number;
  tileValueSelected: string;
  localeCode: string;
}

export interface IFetchError {
  status: number;
  statusText: string;
}

export interface priceProps {
  partNumber: string;
  pricingIndicator: string;
  pricingModel: number;
  scaleQtyPrice: [
    {
      qty: number;
      price: number;
    },
  ];
}

const partsToQueryEssentials = [
  {
    partNumber: 'D0IJYZX',
  },
  {
    partNumber: 'D0IK1ZX',
  },
];

type MarketPlaceRequest = {
  currency: string;
  countryCode3: string;
  parts: typeof partsToQueryEssentials;
};

export const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export const usePrice = ({
  currency,
  country,
  queriesInput,
  recordsInput,
  filterChainsInput,
  monitorsInput,
  httpsRedirectInput,
  tileValueSelected,
  localeCode,
}: PropsFunc) => {
  const [totalCost, setTotalCost] = useCurrencyWithSymbol(
    currency,
    localeCode,
    0,
  );
  const [totalCostMonthly, setTotalCostMonthly] = useCurrencyWithSymbol(
    currency,
    localeCode,
    0,
  );
  const [dataFetched, setDataFetched] = useState([]);
  const [fetchingError, setFetchingError] = useState({});
  const [loading, setLoading] = useState(false);
  const [defaultPrice, setDefaultPrice] = useState(0);

  function calculateTieredPrice(quantity: any, tiers: any) {
    let remaining = quantity;
    let total = 0;

    for (const { limit, price } of tiers) {
      if (remaining <= 0) break;

      const count = Math.min(remaining, limit);
      total += count * price;
      remaining -= count;
    }

    return total;
  }

  // Pricing tiers
  const queryTiers = [
    { limit: 5, price: defaultPrice },
    { limit: 15, price: defaultPrice - 30 },
    { limit: 25, price: defaultPrice - 36 },
    { limit: 50, price: defaultPrice - 40 },
  ];

  const filterChainTiers = [
    { limit: 9, price: defaultPrice - 10 },
    { limit: 15, price: defaultPrice - 22 },
    { limit: 25, price: defaultPrice - 38 },
    { limit: 50, price: defaultPrice - 40 },
  ];

  const monitorTiers = [
    { limit: 48, price: 1.3 },
    { limit: 50, price: 0.07 },
  ];

  const setPricesDataEssentials = (products: priceProps[]) => {
    let price = 0;
    if (products?.length) {
      const priceArray = products.map((item) => ({
        [item.partNumber]: item.scaleQtyPrice,
      }));
      const LPDefault = priceArray[0].D0IJYZX;
      const LPAdditional = priceArray[1].D0IK1ZX;
      setDefaultPrice(LPAdditional[0].price);
      if (
        queriesInput === EST_DEF_QUERIES &&
        tileValueSelected === 'essentials'
      ) {
        price = LPDefault[0]?.price;
      } else if (
        queriesInput > EST_DEF_QUERIES &&
        queriesInput <= 80 &&
        tileValueSelected === 'essentials'
      ) {
        const add = (queriesInput - EST_DEF_QUERIES) / 10;
        price = LPDefault[0]?.price + LPAdditional[0].price * add;
      } else if (
        tileValueSelected === 'standard' &&
        recordsInput >= 1000 &&
        queriesInput >= 50 &&
        filterChainsInput >= 1 &&
        monitorsInput >= 2
      ) {
        const queryPacks = Math.ceil(queriesInput / 10);
        const recordPacks = Math.floor(Math.abs(recordsInput - 1000) / 1000);
        const queryCost = calculateTieredPrice(queryPacks, queryTiers);
        const recordCost = recordPacks * 50;
        const filterChainCost = calculateTieredPrice(
          filterChainsInput - 1,
          filterChainTiers,
        );
        const monitorCost = calculateTieredPrice(
          monitorsInput - 2,
          monitorTiers,
        );
        const totalCost =
          queryCost + recordCost + filterChainCost + monitorCost;
        price = Math.round(totalCost + LPDefault[0]?.price);
      } else if (
        tileValueSelected === 'essentials' &&
        (queriesInput < estMinVal.queryVal ||
          recordsInput < estMinVal.recordsVal ||
          monitorsInput < estMinVal.monitorVal ||
          filterChainsInput < estMinVal.filterVal ||
          httpsRedirectInput! < estMinVal.httpsVal)
      ) {
        price = LPDefault[0]?.price;
      } else if (
        tileValueSelected === 'standard' &&
        (queriesInput < stdMinVal.queryVal ||
          recordsInput < stdMinVal.recordsVal ||
          monitorsInput < stdMinVal.monitorVal ||
          filterChainsInput < stdMinVal.filterVal ||
          httpsRedirectInput! < stdMinVal.httpsVal)
      ) {
        if (queriesInput < 50) {
          queriesInput = 50;
        }
        if (recordsInput < 100) {
          recordsInput = 1000;
        }
        const queryPacks = Math.ceil(queriesInput / 10);
        const recordPacks = Math.floor(Math.abs(recordsInput - 1000) / 1000);
        const queryCost = calculateTieredPrice(queryPacks, queryTiers);
        const recordCost = recordPacks * 50;
        const filterChainCost = calculateTieredPrice(
          filterChainsInput - 1,
          filterChainTiers,
        );
        const monitorCost = calculateTieredPrice(
          monitorsInput - 2,
          monitorTiers,
        );
        const totalCost =
          queryCost + recordCost + filterChainCost + monitorCost;
        price = Math.round(totalCost + LPDefault[0]?.price);
      }
      return Number(price);
    }
  };

  const fetchPrices = () => {
    setLoading(true);
    const body: MarketPlaceRequest = {
      currency: currency,
      countryCode3: country,
      parts: partsToQueryEssentials,
    };
    getListPrice(
      `https://api.marketplace.ibm.com/purchase/catalog/resources/price/info`,
      JSON.stringify(body),
      'POST',
    )
      .then((data) => {
        if (data.parts && data.parts.length !== 0) {
          setLoading(false);
          setDataFetched(data.parts);
          const price = setPricesDataEssentials(data.parts);
          setTotalCost(Number(price) * 12);
          setTotalCostMonthly(Number(price));
        } else {
          setLoading(false);
          setDataFetched([]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setDataFetched([]);
        if (error.response) {
          setFetchingError(error.response);
        }
      });
  };

  useEffect(() => {
    if (currency && dataFetched.length === 0) {
      fetchPrices();
    }
  }, [currency]);

  useEffect(() => {
    const price = setPricesDataEssentials(dataFetched);
    setTotalCost(Number(price) * 12);
    setTotalCostMonthly(Number(price));
  }, [
    queriesInput,
    recordsInput,
    filterChainsInput,
    monitorsInput,
    currency,
    httpsRedirectInput,
    tileValueSelected,
  ]);

  return [totalCost, totalCostMonthly, loading, fetchingError];
};
